import { useEffect, useState, type ReactNode } from 'react';
import {
  useApproveCaptureMutation, useCaptureQuery, useCrmTagsQuery,
  useEditCaptureMutation, useRejectCaptureMutation, useRetryCaptureMutation, useReopenCaptureMutation,
  useUpdateContactTypeMutation,
  type CaptureCommercialStage, type CaptureDetailResponse, type CaptureDetailView,
  type CaptureEdit, type CaptureExecutionResult, type CaptureIntent,
  type CaptureParticipantView, type CaptureStatus, type ConversationPersonView, type KeapContactTypeRead,
  type CaptureClassificationSource, type CrmTagVocabulary,
} from './api.js';
import { CrmTagPicker, EMPTY_VOCABULARY } from './CrmTagPicker.js';
import {
  fromSelection, idFor, labelFor, reachesCrm, stageLabel, toSelection, withdrawnNote,
} from './crmTagSelection.js';
import { PhoneList, PhoneListEditor } from './PhoneListEditor.js';
import { ContactTypeField } from './ContactTypeField.js';
import { SharedAddressConfirm, sharedStateOf } from './SharedAddress.js';
import { contactTypeBlocks, contactTypeView, labelOf, type ContactTypeOptionsLike } from './contactType.js';
import {
  fromCapturePhones, phonesChanged, serializePhones, type PhoneDeliveryLine, type PhoneRow,
} from './phoneList.js';
import {
  DANGER, DrawerActions, DrawerFooter, DrawerSource, useDrawerDirty, useDrawerDone,
} from './ReviewDrawer.js';
import { Alert, Button, Spinner, formatWhen } from './ui.js';

/**
 * Review for email → CRM captures.
 *
 * Every item here is an email the customer chose to send to their CRM, by marking it in
 * their mailbox. That changes what this screen is for: it no longer asks "what did the AI
 * think of this message", it asks "is this what you want in your CRM" — the people found,
 * the company, why they wrote, and exactly which records, tags and note would result.
 */

/** Mirrors INTENT_LABELS in @signal/domain-email, so the select reads like the CRM note. */
export const INTENT_LABELS: Record<CaptureIntent, string> = {
  buying_inquiry: 'Buying inquiry',
  support_request: 'Support request',
  billing: 'Billing',
  scheduling: 'Scheduling',
  seller_outreach: 'Vendor outreach',
  recruiting: 'Recruiting',
  marketing_broadcast: 'Marketing',
  transactional: 'Transactional',
  general: 'General correspondence',
  unknown: 'Email',
};
const INTENTS = Object.keys(INTENT_LABELS) as CaptureIntent[];

/** Review reasons, in words a business user can act on. Never the raw code. */
const REASON_TEXT: Record<string, string> = {
  review_mode: 'Review mode',
  identity_ambiguous: 'More than one Keap contact has this address — untick it or fix the duplicate in Keap',
  no_contact: 'Someone could not be matched to one Keap contact — check who is included',
  quoted_evidence: 'Some details came from earlier messages in the thread',
  forwarded_contact: 'Includes a person from a forwarded message',
  role_mailbox: 'This email may be shared. Confirm the person before sending.',
  shared_address_in_use: 'More than one Keap contact uses this email. Choose the correct person.',
  internal_participant: 'Includes a colleague',
  automated_sender: 'Automated sender',
  enrichment_conflict: 'Email details differ from the CRM',
  extraction_unavailable: 'The email could not be read automatically — check the details',
  ungrounded_claims: 'Some AI details could not be verified and were dropped',
  injection_suspected: 'The email contained instructions aimed at software',
  consumer_domain_company: 'Personal email address',
  no_eligible_contact: 'No contact selected',
  identity_unresolved: 'Keap could not be checked for someone included — untick them',
  missing_identity: 'No email address',
  stale_plan: 'Changed since approval',
  policy_engine_review: 'Needs a person to confirm',
  global_disabled: 'CRM sending is off',
  workspace_not_enabled: 'CRM sending is off',
  pre_activation: 'Approved before CRM sending was enabled',
  rollout_limit_reached: 'Daily CRM limit reached',
  marker_stage_conflict: 'Two labels on this email point to different tags — choose one',
  crm_stage_conflict: 'Your CRM already has this contact with a different tag',
  contact_type_required: 'Choose a Contact Type for each new contact',
};
export const reasonText = (r: string) => REASON_TEXT[r] ?? r.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const STATUS_STYLE: Record<CaptureStatus, { label: string; cls: string }> = {
  review_required: { label: 'Needs review', cls: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  auto_pending: { label: 'Sending automatically', cls: 'bg-sky-50 text-sky-800 ring-sky-600/20' },
  approved: { label: 'Approved', cls: 'bg-sky-50 text-sky-800 ring-sky-600/20' },
  executed: { label: 'Sent to CRM', cls: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20' },
  partially_executed: { label: 'Partly sent', cls: 'bg-orange-50 text-orange-800 ring-orange-600/20' },
  rejected: { label: 'Rejected', cls: 'bg-slate-100 text-slate-700 ring-slate-500/20' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-800 ring-red-600/20' },
  held: { label: 'Waiting to send', cls: 'bg-slate-100 text-slate-700 ring-slate-500/20' },
};

export function CaptureStatusBadge({ status }: { status: CaptureStatus }) {
  const s = STATUS_STYLE[status] ?? { label: status, cls: 'bg-slate-100 text-slate-700 ring-slate-500/20' };
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${s.cls}`}>
      {s.label}
    </span>
  );
}

const ROLE_TEXT: Record<CaptureParticipantView['role'], string> = {
  sender: 'Sender', to: 'To', cc: 'CC', reply_to: 'Reply-to', forwarded_signature: 'Forwarded',
};

/**
 * Where a detail came from, said only when it changes how far to trust it. A signature is
 * the ordinary source and needs no note; a detail inferred from the message text or from
 * an earlier message in the thread is worth a second look.
 */
const SOURCE_HINT: Partial<Record<string, string>> = {
  body: 'from the message text',
  quoted_signature: 'from an earlier message in the thread',
  human_correction: 'edited by you',
};
const hintFor = (source: string) => (SOURCE_HINT[source]
  ? <span className="text-slate-400"> ({SOURCE_HINT[source]})</span> : null);

const EVIDENCE_TEXT: Record<string, string> = {
  signature: 'Signature', body: 'Message text', shared_domain: 'Shared email domain',
  website: 'Website', crm: 'Your CRM', human: 'Reviewer',
};

const EXCLUSION_TEXT: Record<string, string> = {
  internal: 'Internal — not proposed',
  role_mailbox: 'Shared mailbox — not proposed',
  automated_sender: 'Automated sender — not proposed',
  no_address: 'No email address — cannot be proposed',
  reviewer_removed: 'Removed by a reviewer',
  participant_limit: 'Not proposed — too many people on this email',
};

export const PROVIDER_TEXT = { gmail: 'Gmail', microsoft: 'Outlook' } as const;

/** Long addresses, URLs and company names wrap instead of widening the page. */
export const WRAP = 'min-w-0 break-words [overflow-wrap:anywhere]';

/** The exact local moment, weekday and zone included — what "when was this email" means. */
export function exactWhen(iso: string | null): string {
  if (!iso) return 'Time unavailable';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Time unavailable';
  return d.toLocaleString(undefined, {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });
}

export function personName(p: CaptureParticipantView): string {
  return p.displayName
    ?? ([p.givenName, p.familyName].filter(Boolean).join(' ') || p.email || 'Unnamed person');
}

/* ── The detail ────────────────────────────────────────────────────── */

/** What a reviewer did about a shared address: confirm or skip the person, or choose their Keap contact. */
export type SharedAction = { confirm: boolean } | { keapContactId: string };

export interface PersonDraft {
  include: boolean;
  /** A new contact's CRM contact type, as the CRM names it. Null: not chosen. */
  contactType: string | null;
  /** Where that type came from: the person, or the workspace default pre-filled for them. */
  contactTypeSource?: 'user' | 'workspace_default' | null;
  /** This person's own CRM Tags (selection ids), where the screen offers them per person. */
  tags?: string[];
  displayName: string;
  /** Every number, in order, each with its type — edited with the one phone editor. */
  phones: PhoneRow[];
  title: string;
  company: string;
  website: string;
}

interface Draft {
  people: Record<string, PersonDraft>;
  intent: CaptureIntent;
  summary: string;
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
  companyName: string;
  companyWebsite: string;
}

function draftFrom(c: CaptureDetailView, workspaceDefault: string | null = null): Draft {
  const people: Record<string, PersonDraft> = {};
  for (const p of c.participants) {
    people[p.id] = {
      include: p.include,
      // Saved stays saved; a new person with none starts on the workspace default, if any.
      ...(p.contactType
        ? { contactType: p.contactType, contactTypeSource: p.contactTypeSource === 'workspace_default' ? 'workspace_default' as const : 'user' as const }
        : p.crm.kind === 'new' && workspaceDefault
          ? { contactType: workspaceDefault, contactTypeSource: 'workspace_default' as const }
          : { contactType: null, contactTypeSource: null }),
      displayName: p.displayName ?? '',
      phones: fromCapturePhones(p.phones),
      title: p.title?.value ?? '',
      company: p.company?.value ?? '',
      website: p.website?.value ?? '',
    };
  }
  return {
    people,
    intent: c.intent ?? 'unknown',
    summary: c.summary ?? '',
    commercialStage: c.commercialStage,
    relationshipTypeKeys: [...c.relationshipTypeKeys],
    crmTagKeys: [...c.crmTagKeys],
    companyName: c.company?.name ?? '',
    companyWebsite: c.company?.website ?? '',
  };
}

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/**
 * Only what changed, so a correction is recorded as what the reviewer actually did —
 * the server audits which kinds of field moved, and an unchanged name sent back is not a
 * correction anybody made.
 */
function editFrom(c: CaptureDetailView, d: Draft, workspaceDefault: string | null = null): { body: CaptureEdit; dirty: boolean; needsSave: boolean } {
  const base = draftFrom(c, workspaceDefault);
  // A pre-filled default differs from what is saved without being a change on screen.
  let prefilled = false;
  let realChanges = 0;
  const participants: CaptureEdit['participants'] = [];
  for (const p of c.participants) {
    const now = d.people[p.id];
    const was = base.people[p.id];
    if (!now || !was) continue;
    const e: CaptureEdit['participants'][number] = { id: p.id, include: now.include };
    let changed = now.include !== was.include;
    const text = (k: 'displayName' | 'title' | 'company' | 'website') => {
      if (now[k].trim() !== was[k].trim()) { e[k] = now[k].trim() || null; changed = true; }
    };
    text('displayName'); text('title'); text('company'); text('website');
    if (now.contactType !== (p.contactType ?? null)) {
      e.contactType = now.contactType;
      if (now.contactType) e.contactTypeSource = now.contactTypeSource === 'workspace_default' ? 'workspace_default' : 'user';
      if (now.contactType === was.contactType) prefilled = true; else changed = true;
    }
    // The whole list, in order, with each number's type — sent only when it changed.
    if (phonesChanged(now.phones, was.phones)) {
      e.phones = serializePhones(now.phones); changed = true;
    }
    if (changed) realChanges += 1;
    if (changed || e.contactType !== undefined) participants.push(e);
  }
  const body: CaptureEdit = {
    expectedUpdatedAt: c.updatedAt,
    participants,
    commercialStage: d.commercialStage,
    relationshipTypeKeys: d.relationshipTypeKeys,
    crmTagKeys: d.crmTagKeys,
  };
  let dirty = realChanges > 0;
  if (d.intent !== base.intent) { body.intent = d.intent; dirty = true; }
  if (d.summary.trim() !== base.summary.trim()) { body.summary = d.summary.trim() || null; dirty = true; }
  if (d.commercialStage !== base.commercialStage) dirty = true;
  if (!sameSet(d.relationshipTypeKeys, base.relationshipTypeKeys)) dirty = true;
  if (!sameSet(d.crmTagKeys, base.crmTagKeys)) dirty = true;
  if (c.company && (d.companyName.trim() !== base.companyName.trim()
    || d.companyWebsite.trim() !== base.companyWebsite.trim())) {
    body.company = {
      ...(d.companyName.trim() !== base.companyName.trim() ? { name: d.companyName.trim() || null } : {}),
      ...(d.companyWebsite.trim() !== base.companyWebsite.trim() ? { website: d.companyWebsite.trim() || null } : {}),
    };
    dirty = true;
  }
  return { body, dirty, needsSave: dirty || prefilled };
}

export type Outcome = {
  tone: 'success' | 'error' | 'info'; text: string; retry?: boolean; reload?: boolean;
  /** A number the server refused, for the person it belongs to. Shown beside their numbers. */
  phone?: { participantId: string; message: string; values: string[] };
};

const ERROR_TEXT: Record<string, string> = {
  stale: 'This capture changed while you were looking at it. Reload to see the current version.',
  not_editable: 'This capture has already been decided, so it can no longer be edited.',
  internal_participant: 'A colleague cannot be added as a CRM contact.',
  no_address: 'A person without an email address cannot be added as a CRM contact.',
  invalid_phone: 'One of the phone numbers could not be read. Include the country code, for example +1 415 555 0142.',
  invalid_website: 'One of the websites is not a valid address.',
  unknown_crm_option: 'One of the CRM choices is no longer available. Reload and choose again.',
  stale_plan: 'This capture changed; reload it before approving.',
  not_approvable: 'This capture has already been decided.',
  nothing_selected: 'Select at least one person to include before approving.',
  not_rejectable: 'This capture can no longer be rejected.',
  contact_type_required: 'Choose a Contact Type for each new contact before approving.',
  unknown_contact_type: 'A chosen Contact Type is not available in your CRM. Choose one from the list.',
  contact_type_unavailable: 'Contact types could not be loaded from your CRM. Try again in a moment.',
  contact_type_existing: 'This person is already in your CRM, so their Contact Type stays as it is there.',
  contact_type_unsupported: 'Your CRM has no Contact Type to choose.',
  role_mailbox: 'This email may be shared. Confirm the person before sending.',
  shared_address_unconfirmed: 'This email may be shared. Confirm the person before sending.',
  shared_address_in_use: 'More than one Keap contact uses this email. Choose the correct person.',
  shared_address_needs_name: 'Add the person’s name before confirming this email belongs to them.',
  unknown_keap_contact: 'That Keap contact is no longer a match for this email. Reload and choose again.',
  identity_ambiguous: 'More than one Keap contact matches this email. Untick Include for this person, or merge the duplicates in Keap first.',
  identity_unresolved: 'Keap couldn’t be checked for someone included, so it isn’t known whether they’re new. Untick Include for them.',
};

export function errorOutcome(e: unknown): Outcome {
  const data = (e as { data?: { error?: string; participantId?: string; message?: string; phones?: unknown } })?.data;
  const code = data?.error ?? '';
  const status = (e as { status?: number })?.status;
  if (status === 403) return { tone: 'error', text: 'You do not have permission to do that.' };
  if (code === 'invalid_phone' && data?.participantId) {
    return {
      tone: 'error',
      text: 'One of the phone numbers could not be read. Fix or remove it, then try again.',
      phone: {
        participantId: data.participantId,
        message: data.message ?? 'One of the phone numbers is not a valid number. Fix or remove it to continue.',
        values: Array.isArray(data.phones) ? data.phones.map(String) : [],
      },
    };
  }
  return {
    tone: 'error',
    text: ERROR_TEXT[code] ?? 'That could not be completed. Try again.',
    reload: code === 'stale' || code === 'stale_plan' || code === 'unknown_crm_option',
  };
}

/** What the server says happened, never what the button asked for. */
export function executionOutcome(r: CaptureExecutionResult): Outcome {
  if (r.refusal === 'stale_plan') {
    return { tone: 'error', text: 'This capture changed; reload it before approving.', reload: true };
  }
  if (r.refusal === 'needs_person') {
    return { tone: 'error', text: 'Nothing more was sent. Someone included can’t be sent as they are — the reason is shown beside them. Change it, then approve again.', reload: true };
  }
  if (r.refusal === 'contact_type_required') {
    return { tone: 'error', text: 'Nothing was sent. Choose a Contact Type your CRM offers for each new contact, then approve again.', reload: true };
  }
  if (!r.crmWriteEnabled || r.refusal === 'global_disabled' || r.refusal === 'workspace_not_enabled') {
    return { tone: 'info', text: 'Approved. Nothing was sent to your CRM because CRM sending is switched off.' };
  }
  if (r.refusal === 'pre_activation') {
    return {
      tone: 'info',
      text: 'Approved, and held: this approval predates CRM sending being switched on for your workspace, so it was not sent automatically. Retry to send it now.',
      retry: true,
    };
  }
  if (r.status === 'executed') return { tone: 'success', text: 'Sent to your CRM.' };
  if (r.status === 'partially_executed' || r.status === 'failed' || r.status === 'held') {
    return {
      tone: 'error',
      text: `${STATUS_STYLE[r.status].label}. ${r.status === 'partially_executed'
        ? 'Some of it reached your CRM and some did not.'
        : r.status === 'failed' ? 'Nothing was confirmed by your CRM.' : 'It was not sent yet.'} You can retry.`,
      retry: true,
    };
  }
  return { tone: 'info', text: `Recorded. Status: ${r.status ? (STATUS_STYLE[r.status]?.label ?? r.status) : 'unknown'}.` };
}

export const inputCls = 'mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm disabled:bg-slate-50 disabled:text-slate-500';
export const h4 = 'text-xs font-semibold uppercase tracking-wide text-slate-500';

/** Where a chosen tag came from, as a short hint on its chip. Nothing for "none". */
const TAG_SOURCE_HINT: Partial<Record<CaptureClassificationSource, string>> = {
  marker: 'from label',
  crm: 'from your CRM',
  default: 'default',
};

/**
 * The hint for each tag the capture arrived with, while the person has not changed that
 * part of the selection. Once they have, the tags are theirs and no hint is shown.
 */
export function tagHints(c: CaptureDetailView, d: Draft, vocab: CrmTagVocabulary): Record<string, string> {
  const s = c.classificationSources;
  if (!s) return {};
  const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));
  const out: Record<string, string> = {};
  const put = (id: string, src: CaptureClassificationSource) => {
    const text = TAG_SOURCE_HINT[src];
    if (text && reachesCrm(id, vocab)) out[id] = text;
  };
  if (c.commercialStage && d.commercialStage === c.commercialStage) put(idFor('stage', c.commercialStage), s.commercialStage);
  if (same(d.relationshipTypeKeys, c.relationshipTypeKeys)) for (const k of c.relationshipTypeKeys) put(idFor('type', k), s.relationshipTypes);
  if (same(d.crmTagKeys, c.crmTagKeys)) for (const k of c.crmTagKeys) put(idFor('tag', k), s.crmTags);
  return out;
}

function CrmBadge({ p }: { p: CaptureParticipantView }) {
  const k = p.crm.kind;
  // Words first, with a mark for those who scan by shape: never colour alone.
  const [text, cls] = p.isRoleMailbox && !p.isInternal && !p.sharedAddressConfirmation
    ? ['Shared email', 'bg-amber-50 text-amber-800 ring-amber-600/20']
    : k === 'existing'
    ? ['✓ In Keap', 'bg-emerald-50 text-emerald-800 ring-emerald-600/20']
    : k === 'new'
      ? ['New contact', 'bg-sky-50 text-sky-800 ring-sky-600/20']
      : k === 'ambiguous'
        // Plain words: what the person has to do, not the identity layer's name for it.
        ? ['Choose Keap contact', 'bg-amber-50 text-amber-800 ring-amber-600/20']
        : ['Not checked', 'bg-slate-100 text-slate-700 ring-slate-500/20'];
  const mark = text.startsWith('✓ ');
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`} data-crm-badge={k}>
      {mark ? <><span aria-hidden="true">✓ </span>{text.slice(2)}</> : text}
    </span>
  );
}

/** Where a person stands in the CRM, as the Contact Type field needs it. */
export const crmStatusOf = (p: CaptureParticipantView): 'new' | 'existing' | 'other' =>
  p.crm.kind === 'new' ? 'new' : p.crm.kind === 'existing' ? 'existing' : 'other';

/** One person's send, as a person reads it: whether it reached Keap, and if not, why. */
const HELD = new Set(['global_disabled', 'workspace_not_enabled', 'crm_write_disabled', 'pre_activation', 'rollout_limit_reached', 'not_in_rollout_scope']);
/** Stops a person resolves in Review, as opposed to a send the CRM did not complete. */
const FOR_PERSON = new Set(['role_mailbox', 'shared_address_in_use', 'identity_ambiguous', 'identity_unresolved', 'no_contact', 'contact_type_required']);
function DeliveryBadge({ delivery, sharedUnconfirmed }: { delivery: NonNullable<ConversationPersonView['delivery']>; sharedUnconfirmed: boolean }) {
  const held = delivery.blockedBy !== null && HELD.has(delivery.blockedBy);
  const forPerson = delivery.blockedBy !== null && FOR_PERSON.has(delivery.blockedBy);
  // What the person needs, in words: a shared email waiting to be confirmed is not an error.
  const [text, cls] = delivery.state === 'sent'
    ? ['Sent to Keap', 'bg-emerald-50 text-emerald-800 ring-emerald-600/20']
    : delivery.state === 'partly_sent'
      ? ['Partly sent', 'bg-amber-50 text-amber-800 ring-amber-600/20']
      : held ? ['Waiting to send', 'bg-slate-100 text-slate-700 ring-slate-500/20']
        : sharedUnconfirmed ? ['Needs confirmation', 'bg-amber-50 text-amber-800 ring-amber-600/20']
          : forPerson ? ['Needs review', 'bg-amber-50 text-amber-800 ring-amber-600/20']
            : ['Not sent', 'bg-amber-50 text-amber-800 ring-amber-600/20'];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`} data-person-delivery={delivery.state}>{text}</span>;
}

export function PersonRow({ p, d, editable, onChange, deliveries, phoneError, types, roles, messages, delivery, onShared, sharedBusy, tagPicker, renderMessages, keap, proposedType, onProposeType, onRetryRead, hideSelect, statusLine }: {
  p: CaptureParticipantView;
  /** An existing contact's Contact Type as Keap holds it now; with `onProposeType`, changeable by proposal. */
  keap?: KeapContactTypeRead;
  proposedType?: string | null;
  onProposeType?: (to: string | null) => void;
  onRetryRead?: () => void;
  /** Nothing left to select (already sent): no checkbox. */
  hideSelect?: boolean;
  /** What is pending for this person, in words ("2 emails to add"). */
  statusLine?: string | null;
  /**
   * This person's own optional CRM Tags. Absent: the screen shows tags for the whole
   * message elsewhere, as before.
   */
  tagPicker?: { vocab: CrmTagVocabulary; selected: string[]; onChange: (ids: string[]) => void };
  /** This person's emails, opened from the envelope button. Absent: no button. */
  renderMessages?: () => ReactNode;
  /**
   * A shared address (admin@, info@): confirm it is this person's, skip them, or choose
   * the Keap contact that is them. Each is saved at once, so Keap's answer is shown next.
   */
  onShared?: (action: SharedAction) => void;
  sharedBusy?: boolean;
  /** Where this person's send stands across a conversation, when any message tried. */
  delivery?: ConversationPersonView['delivery'];
  d: PersonDraft;
  /** The CRM's contact types; absent or unsupported shows no Contact Type at all. */
  types?: ContactTypeOptionsLike | null;
  /** Every role across a conversation; defaults to this message's one. */
  roles?: ReadonlyArray<CaptureParticipantView['role']>;
  /** How many marked messages of the conversation this person is on. */
  messages?: number;
  editable: boolean;
  onChange: (patch: Partial<PersonDraft>) => void;
  /** What will happen to each of this person's numbers. */
  deliveries?: readonly PhoneDeliveryLine[];
  /** The server's refusal of one of this person's numbers, from the last save. */
  phoneError?: { message: string; values: string[] } | null;
}) {
  const [editing, setEditing] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  // A refused number opens this person's details, so the error is beside the number.
  useEffect(() => { if (phoneError) setEditing(true); }, [phoneError]);
  const blocked = p.isInternal || !p.email;
  // A shared address is added by confirming whose it is, never by a bare tick.
  const shared = p.isRoleMailbox && !p.isInternal && Boolean(p.email);
  const renamed = shared && Boolean(p.sharedAddressConfirmation) && d.displayName.trim() !== (p.displayName ?? '').trim();
  const idBase = `person-${p.id.replace(/[^A-Za-z0-9_-]/g, '_')}`;
  const field = (k: 'displayName' | 'title' | 'company' | 'website', label: string, type = 'text') => (
    <label className="block min-w-0 text-sm text-slate-700" htmlFor={`${idBase}-${k}`}>
      {label}
      <input
        id={`${idBase}-${k}`}
        type={type}
        value={d[k]}
        disabled={!editable}
        onChange={(e) => onChange({ [k]: e.target.value })}
        className={inputCls}
      />
    </label>
  );

  // Selected for sending: the one checkbox, beside the name. A shared address is added by confirming who it is.
  const selectable = !shared || Boolean(p.sharedAddressConfirmation && !renamed);
  const name = personName(p);
  const count = messages ?? 1;
  return (
    <li className="min-w-0 rounded-md border border-slate-200 p-3" data-capture-person="">
      <div className="flex min-w-0 items-start gap-3">
        {selectable && !blocked && !hideSelect ? (
          // A 44px hit area around a 20px box; the name beside it is the label's text for screen readers.
          <label className="-my-2.5 -ml-2.5 inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
            <input
              type="checkbox" checked={d.include} disabled={!editable}
              onChange={(e) => onChange({ include: e.target.checked })}
              aria-label={`Select ${name}`} data-person-select=""
              className="h-5 w-5 rounded border-slate-300"
            />
          </label>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={`text-sm font-medium text-slate-900 ${WRAP}`}>{name}</p>
              {p.email ? <p className={`text-xs text-slate-600 ${WRAP}`}>{p.email}</p> : null}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <CrmBadge p={p} />
              {delivery ? <DeliveryBadge delivery={delivery} sharedUnconfirmed={p.isRoleMailbox && !p.sharedAddressConfirmation} /> : null}
              {renderMessages ? (
                <button
                  type="button" aria-expanded={showMessages} onClick={() => setShowMessages((v) => !v)}
                  aria-label={`${showMessages ? 'Hide' : 'Show'} ${count} email${count === 1 ? '' : 's'} with ${name}`}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                  data-person-messages-toggle=""
                >
                  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <rect x="2.5" y="4.5" width="15" height="11" rx="1.5" /><path d="M3 5.5l7 5.5 7-5.5" />
                  </svg>
                  <span>{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          {roles && roles.length > 0 ? (
            <p className="mt-0.5 text-xs text-slate-500">{roles.map((r) => ROLE_TEXT[r]).join(' · ')}</p>
          ) : null}
          {statusLine ? <p className="mt-0.5 text-xs text-slate-700" data-person-status="">{statusLine}</p> : null}
        </div>
      </div>
      {renderMessages && showMessages ? <div className="mt-2 min-w-0" data-person-messages="">{renderMessages()}</div> : null}

      <dl className="mt-2 space-y-0.5 text-xs text-slate-600">
        {p.phones.length > 0 && !editing ? (
          <div className={WRAP}>
            <dt className="sr-only">Phone numbers</dt>
            <dd><PhoneList phones={p.phones} deliveries={deliveries} />{hintFor(p.phones[0]!.source)}</dd>
          </div>
        ) : null}
        {p.title ? (
          <div className={WRAP}><dt className="inline">Title: </dt>
            <dd className="inline">{p.title.value}{hintFor(p.title.source)}</dd></div>
        ) : null}
        {p.company ? (
          <div className={WRAP}><dt className="inline">Company: </dt><dd className="inline">{p.company.value}</dd></div>
        ) : null}
        {p.website ? (
          <div className={WRAP}><dt className="inline">Website: </dt><dd className="inline">{p.website.value}</dd></div>
        ) : null}
        {p.isRoleMailbox && !p.isInternal
          ? null
          : p.crm.kind === 'ambiguous'
            ? <div data-person-block="">More than one Keap contact has this address. ContactPilot won’t pick one — untick Include, or merge the duplicates in Keap.</div>
            : p.crm.kind === 'unresolved' && !p.isInternal
              ? <div data-person-block="">Keap could not be checked for this person.</div> : null}
      </dl>

      {p.isInternal ? (
        <p className="mt-2 text-sm font-medium text-slate-600">Internal — not proposed</p>
      ) : null}
      {shared ? (
        <SharedAddressConfirm
          name={d.displayName} email={p.email!} idBase={idBase}
          // Renamed since it was confirmed: the confirmation was of the old name, so it is asked again.
          state={renamed ? { confirmed: false, match: null, candidates: [] } : sharedStateOf(p)}
          editable={editable && Boolean(onShared)} busy={sharedBusy}
          onConfirm={() => onShared?.({ confirm: true })}
          onSkip={() => onShared?.({ confirm: false })}
          onChoose={(keapContactId) => onShared?.({ keapContactId })}
        />
      ) : null}
      {!shared && !d.include && p.exclusionReason && !p.isInternal ? (
        <p className="text-xs text-slate-500">{EXCLUSION_TEXT[p.exclusionReason] ?? p.exclusionReason}</p>
      ) : null}
      {/* An existing contact's type is shown (and changeable) whether or not they are selected. */}
      {(d.include || (keap && onProposeType && crmStatusOf(p) === 'existing')) && !p.isInternal && p.email ? (
        <div className="mt-2">
          <ContactTypeField
            types={types}
            status={crmStatusOf(p)}
            value={d.contactType}
            existingValue={keap?.contactType ?? p.existingContactType ?? null}
            onChange={(contactType) => onChange({ contactType, contactTypeSource: 'user' })}
            disabled={!editable && !onProposeType}
            idBase={idBase}
            {...(keap && onProposeType ? { keap, proposed: proposedType ?? null, onPropose: onProposeType, ...(onRetryRead ? { onRetryRead } : {}) } : {})}
          />
          {d.contactTypeSource === 'workspace_default' && d.contactType && crmStatusOf(p) === 'new' ? (
            <p className="text-xs text-slate-500" data-contact-type-default="">Your workspace default. Change it for this person if needed.</p>
          ) : null}
        </div>
      ) : null}
      {tagPicker && d.include && !p.isInternal && p.email ? (
        <div className="mt-2 min-w-0" data-person-tags="">
          <CrmTagPicker
            vocab={tagPicker.vocab} selected={tagPicker.selected} onChange={tagPicker.onChange}
            disabled={!editable} legend={`CRM Tags for ${name} (optional)`} legendClassName="text-sm text-slate-700"
            empty="No tags. Add one if you want."
          />
        </div>
      ) : null}

      {p.enrichment.length > 0 ? (
        <div className="mt-2 rounded bg-slate-50 p-2 text-xs text-slate-600">
          <ul className="space-y-0.5">
            {p.enrichment.map((e) => (
              <li key={`${e.field}-${e.proposed}`} className={WRAP}>
                {e.current
                  ? <>CRM has {e.current} → email shows {e.proposed}{e.conflict ? ' (conflict)' : ''}</>
                  : <>CRM has no {e.field === 'jobTitle' ? 'title' : e.field} → email shows {e.proposed}</>}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-slate-500">Contact updates are not sent; shown for reference.</p>
        </div>
      ) : null}

      {editable && !p.isInternal ? (
        <div className="mt-1">
          <button
            type="button"
            aria-expanded={editing}
            onClick={() => setEditing((v) => !v)}
            className="inline-flex min-h-11 items-center text-sm text-slate-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            {editing ? 'Done editing' : 'Edit details'}
          </button>
          {editing ? (
            <div className="mt-1 min-w-0 space-y-4">
              <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                {field('displayName', 'Name')}
                {field('title', 'Job title')}
                {field('company', 'Company')}
                {field('website', 'Website', 'url')}
              </div>
              <PhoneListEditor
                rows={d.phones}
                onChange={(phones) => onChange({ phones })}
                deliveries={deliveries}
                error={phoneError}
                disabled={!editable}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * One captured email, as the Review drawer shows it. The drawer owns the frame — its title,
 * Close, Escape and focus — so this is only the content and its decisions.
 */
export function CaptureDetail({ id }: { id: string }) {
  const { data, isLoading, isFetching, error, refetch } = useCaptureQuery(id);
  const tags = useCrmTagsQuery();
  const [edit, { isLoading: saving }] = useEditCaptureMutation();
  const [approve, { isLoading: approving }] = useApproveCaptureMutation();
  const [retry, { isLoading: retrying }] = useRetryCaptureMutation();
  const [reopen, { isLoading: reopening }] = useReopenCaptureMutation();
  const [updateType, { isLoading: updatingType }] = useUpdateContactTypeMutation();
  const [proposals, setProposals] = useState<Record<string, { ext: string; from: string | null; to: string; requestId: string }>>({});
  const [reject, { isLoading: rejecting }] = useRejectCaptureMutation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [showMessage, setShowMessage] = useState(false);
  const done = useDrawerDone();
  const vocab = tags.data ?? EMPTY_VOCABULARY;

  const capture = data?.capture ?? null;

  // A different capture is a different draft; never carry one person's edits onto another.
  useEffect(() => { setOutcome(null); setShowMessage(false); setProposals({}); }, [id]);
  /*
   * Re-seeded whenever the server's version changes — after a save, or after a reload
   * following a stale write — so the form always shows what approval would bind to.
   */
  const captureVersion = capture ? `${capture.id}@${capture.updatedAt}` : null;
  const workspaceDefault = data?.contactTypes?.workspaceDefault ?? null;
  useEffect(() => { if (capture) setDraft(draftFrom(capture, workspaceDefault)); }, [captureVersion, workspaceDefault]);
  /*
   * A proposal is of one contact and one value as seen. If Keap's value or the matched
   * contact moved since, it is dropped and the person is told, never carried over.
   */
  useEffect(() => {
    if (!data) return;
    const stale = Object.entries(proposals).filter(([pid, x]) => {
      const p = data.capture.participants.find((q) => q.id === pid);
      const live = data.keapContactTypes?.[x.ext];
      return !p || p.crm.kind !== 'existing' || p.crm.externalContactId !== x.ext || !live || live.status !== 'ok' || live.contactType !== x.from;
    });
    if (stale.length === 0) return;
    setProposals((all) => Object.fromEntries(Object.entries(all).filter(([pid]) => !stale.some(([s]) => s === pid))));
    setOutcome({ tone: 'error', text: 'This contact changed in Keap. Review the latest information before continuing.' });
  }, [data]);

  const busy = saving || approving || retrying || rejecting || reopening || updatingType;
  const editable = capture?.status === 'review_required';
  // Stopped for a person: "Review and fix" reopens it. Anything else unfinished may be retried.
  const canReopen = capture?.stoppedBy === 'person';
  const canRetry = capture ? ['partially_executed', 'failed', 'held'].includes(capture.status) && !canReopen : false;
  const canReject = capture ? ['review_required', 'held', 'failed'].includes(capture.status) : false;
  const pending = capture && draft ? editFrom(capture, draft, workspaceDefault) : null;
  // Closing the drawer with unsaved corrections asks first.
  useDrawerDirty(Boolean(editable && pending?.dirty));
  /*
   * A tag that is no longer available blocks approval until it is removed, and so does a
   * new contact with no Contact Type where the CRM needs one.
   */
  const blocked = editable && draft && capture
    ? (withdrawnNote(toSelection(draft, vocab), vocab)
      ?? contactTypeBlocks(capture.participants
        .filter((p) => draft.people[p.id]?.include && !p.isInternal && p.email)
        .map((p) => contactTypeView({
          types: data?.contactTypes, status: crmStatusOf(p), value: draft.people[p.id]?.contactType,
          existingValue: p.existingContactType ?? null,
        }))))
    : null;

  const reload = async () => {
    setOutcome(null);
    await refetch();
  };

  const onSave = async (): Promise<CaptureDetailView | null> => {
    if (!capture || !pending) return null;
    try {
      const res = await edit({ id, body: pending.body }).unwrap();
      return res.capture;
    } catch (e) {
      setOutcome(errorOutcome(e));
      return null;
    }
  };

  /**
   * A decision that went through closes the drawer and says so in the list. Anything that
   * still needs this person — an error, a held send to retry, a reload — keeps it open.
   */
  const settle = (o: Outcome) => {
    if (done && o.tone !== 'error' && !o.retry && !o.reload) done(o.text);
    else setOutcome(o);
  };

  /** Proposed Contact Type changes for people already in Keap: sent only by an approval. */
  const runTypeUpdates = async (): Promise<string | null> => {
    const out: string[] = [];
    const types = data?.contactTypes;
    const label = (v: string | null) => (v ? (types ? labelOf(types, v) : v) : 'Not set');
    for (const [pid, x] of Object.entries(proposals)) {
      try {
        const r = await updateType({ externalContactId: x.ext, expected: x.from, contactType: x.to, requestId: x.requestId, source: { kind: 'email', captureId: id, personId: pid } }).unwrap();
        out.push(r.status === 'checking'
          ? 'Keap did not confirm the Contact Type change yet. ContactPilot will check before doing anything else. Review the result later.'
          : `Contact Type updated in Keap (${label(x.from)} → ${label(x.to)}).`);
      } catch (e) {
        const body = (e as { data?: { error?: string; current?: string | null; message?: string } })?.data;
        out.push(body?.error === 'contact_type_changed'
          ? `This contact changed in Keap: now ${label(body.current ?? null)}. Review the latest Contact Type before updating.`
          : body?.message ?? 'We couldn’t update the Contact Type. Review the issue before trying again.');
      }
      setProposals((all) => { const n = { ...all }; delete n[pid]; return n; });
    }
    return out.length > 0 ? out.join(' ') : null;
  };

  const onApprove = async () => {
    if (!capture || !pending) return;
    setOutcome(null);
    // Nothing to send but a Contact Type change: just that.
    if (!editable) {
      const t = await runTypeUpdates();
      await refetch();
      if (t) setOutcome({ tone: t.includes('updated in Keap') && !t.includes('couldn') && !t.includes('changed in Keap') ? 'success' : 'error', text: t });
      return;
    }
    let planHash = capture.planHash;
    // Unsaved corrections are saved first, so the approval binds to what is on screen.
    // A pre-filled default is saved too, so the approval carries the type on screen.
    if (pending.needsSave) {
      const saved = await onSave();
      if (!saved) return;
      planHash = saved.planHash;
    }
    try {
      const o = executionOutcome(await approve({ id, planHash }).unwrap());
      const t = Object.keys(proposals).length > 0 ? await runTypeUpdates() : null;
      settle(t ? { ...o, text: `${o.text} ${t}`, ...(t.includes('Review the') ? { tone: 'error' as const, reload: true } : {}) } : o);
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  const onRetry = async () => {
    setOutcome(null);
    try {
      settle(executionOutcome(await retry({ id }).unwrap()));
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  /** "Review and fix": back to Review. Nothing is sent or retried. */
  const onReopen = async () => {
    setOutcome(null);
    try {
      await reopen({ id }).unwrap();
      await refetch();
      setOutcome({ tone: 'info', text: 'Back in Review. Nothing was sent, and what already reached Keap is kept. Make the change shown beside the person, then approve.' });
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  const onReject = async () => {
    setOutcome(null);
    try {
      await reject({ id }).unwrap();
      settle({ tone: 'info', text: 'Rejected. Nothing was sent to your CRM, and the email is unchanged.' });
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  const onSaveOnly = async () => {
    setOutcome(null);
    const saved = await onSave();
    if (saved) setOutcome({ tone: 'success', text: 'Changes saved. Nothing has been sent to your CRM yet.' });
  };

  /** A shared-address decision, saved at once so Keap's answer shows before anything else. */
  const onShared = async (pid: string, action: SharedAction) => {
    if (!capture || !draft) return;
    setOutcome(null);
    const body = editFrom(capture, draft, workspaceDefault).body;
    const mine = sharedEdit(body.participants.find((x) => x.id === pid), pid, draft.people[pid]!, action);
    try {
      await edit({ id, body: { ...body, participants: [...body.participants.filter((x) => x.id !== pid), mine] } }).unwrap();
      await refetch();
      setOutcome(sharedOutcome(action));
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  const secondary: ReactNode[] = [];
  if (editable) {
    secondary.push(
      <Button key="save" variant="secondary" disabled={busy || !pending?.dirty || Boolean(blocked)} onClick={() => void onSaveOnly()}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>,
    );
  }
  if (canReject) {
    secondary.push(
      <Button key="reject" variant="secondary" className={DANGER} disabled={busy} onClick={() => void onReject()}>
        Reject / No action
      </Button>,
    );
  }
  // Nothing left to send but a Contact Type change: the action says it is only that.
  const typeOnly = !editable && Object.keys(proposals).length > 0;
  const primary = editable || typeOnly ? (
    <Button disabled={busy || Boolean(blocked)} onClick={() => void onApprove()}>
      {approving || updatingType ? 'Approving…' : typeOnly ? 'Approve change' : 'Approve selected'}
    </Button>
  ) : canReopen ? (
    <Button disabled={busy} onClick={() => void onReopen()}>
      {reopening ? 'Opening…' : 'Review and fix'}
    </Button>
  ) : canRetry ? (
    <Button disabled={busy} onClick={() => void onRetry()}>
      {retrying ? 'Retrying…' : 'Retry'}
    </Button>
  ) : undefined;

  return (
    <div className="min-w-0" data-capture-detail="">
      {isLoading ? <Spinner label="Loading this capture…" /> : null}
      {error && !data ? <Alert tone="error">This capture could not be loaded.</Alert> : null}

      {data && capture && draft ? (
        <CaptureDetailBody
          data={data}
          vocab={vocab}
          capture={capture}
          draft={draft}
          setDraft={setDraft}
          editable={editable && !busy}
          showMessage={showMessage}
          setShowMessage={setShowMessage}
          phoneError={outcome?.phone ?? null}
          onShared={(pid, a) => void onShared(pid, a)}
          typeChanges={Object.entries(proposals).map(([pid, x]) => {
            const p = capture.participants.find((q) => q.id === pid);
            const label = (v: string | null) => (v ? (data.contactTypes ? labelOf(data.contactTypes, v) : v) : 'Not set');
            const name = (p && draft.people[p.id]?.displayName.trim()) || (p ? personName(p) : 'a contact');
            return `Update existing Keap contact: ${name} — Contact Type ${label(x.from)} → ${label(x.to)}`;
          })}
          typePropsFor={(p) => {
            if (p.crm.kind !== 'existing') return {};
            const ext = p.crm.externalContactId;
            const keap = data?.keapContactTypes?.[ext];
            if (!keap) return {};
            return {
              keap, proposedType: proposals[p.id]?.to ?? null, onRetryRead: () => { void refetch(); },
              onProposeType: (to: string | null) => setProposals((all) => {
                const n = { ...all };
                if (to === null) delete n[p.id];
                else n[p.id] = { ext, from: keap.contactType, to, requestId: n[p.id]?.requestId ?? crypto.randomUUID() };
                return n;
              }),
            };
          }}
          sharedBusy={busy}
          footer={(
            <>
              {capture.stale ? (
                <Alert tone="error">This capture changed since its plan was made. Reload it before approving.</Alert>
              ) : null}
              {editable ? (
                <p className="text-xs text-slate-500">
                  Approve sends the included people, tags and note shown above to your CRM.
                  Reject closes this capture without changing your CRM or the email.
                </p>
              ) : null}
              <DrawerFooter>
                <DrawerActions primary={primary} secondary={secondary}>
                  {blocked ? <p className="text-xs text-amber-900 [overflow-wrap:anywhere]" data-blocked-note="">{blocked}</p> : null}
                  <div aria-live="polite" data-capture-actions="">
                    {outcome ? (
                      <Alert tone={outcome.tone}>
                        {outcome.text}
                        {outcome.reload || capture.stale ? (
                          <button
                            type="button" disabled={isFetching} onClick={() => void reload()}
                            className="ml-2 inline-flex min-h-11 items-center font-medium underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900"
                          >
                            Reload
                          </button>
                        ) : null}
                      </Alert>
                    ) : capture.stale ? (
                      <Button variant="secondary" className="min-h-11" disabled={isFetching} onClick={() => void reload()}>Reload</Button>
                    ) : null}
                  </div>
                </DrawerActions>
              </DrawerFooter>
            </>
          )}
        />
      ) : null}
    </div>
  );
}

function CaptureDetailBody({
  data, vocab, capture, draft, setDraft, editable, showMessage, setShowMessage, footer, phoneError, onShared, sharedBusy, typePropsFor, typeChanges,
}: {
  /** The Contact Type changes approval would make, one line each, in the summary. */
  typeChanges: readonly string[];
  onShared: (pid: string, action: SharedAction) => void;
  /** An existing person's live Contact Type and a way to propose a change, when there is one. */
  typePropsFor: (p: CaptureParticipantView) => Partial<Pick<Parameters<typeof PersonRow>[0], 'keap' | 'proposedType' | 'onProposeType' | 'onRetryRead'>>;
  sharedBusy: boolean;
  data: CaptureDetailResponse;
  vocab: CrmTagVocabulary;
  capture: CaptureDetailView;
  draft: Draft;
  setDraft: (fn: (d: Draft | null) => Draft | null) => void;
  editable: boolean;
  showMessage: boolean;
  setShowMessage: (fn: (v: boolean) => boolean) => void;
  footer: ReactNode;
  phoneError: { participantId: string; message: string; values: string[] } | null;
}) {
  const { source } = data;
  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchPerson = (pid: string, p: Partial<PersonDraft>) =>
    setDraft((d) => (d ? { ...d, people: { ...d.people, [pid]: { ...d.people[pid]!, ...p } } } : d));
  /** One CRM Tags selection over the three fields the capture stores. */
  const tagIds = toSelection(draft, vocab);
  const setTags = (ids: string[]) => {
    const f = fromSelection(ids, vocab);
    patch({ ...f, commercialStage: f.commercialStage as CaptureCommercialStage | null });
  };

  const included = capture.participants.filter((p) => draft.people[p.id]?.include && !p.isInternal && p.email);
  const consequence = included.map((p) => {
    const name = draft.people[p.id]?.displayName.trim() || personName(p);
    if (p.crm.kind === 'existing') return `Existing: ${name}`;
    if (p.crm.kind === 'new') return `Create: ${name}`;
    if (p.isRoleMailbox && !p.sharedAddressConfirmation) return `Needs confirming: ${name} (shared email)`;
    if (p.crm.kind === 'ambiguous') return `Needs a decision: ${name} (more than one CRM contact)`;
    return `Not checked: ${name}`;
  });
  // Only tags that would actually reach the CRM are named as what approval sends.
  const tagLabels = tagIds.filter((tid) => reachesCrm(tid, vocab)).map((tid) => labelFor(tid, vocab));
  const hints = tagHints(capture, draft, vocab);

  /*
   * Where it came from, as context: the label or category the customer applied. Never a
   * classification to manage — what the label means is set in Email Capture.
   */
  const markers = source.markers && source.markers.length > 0
    ? source.markers
    : [...new Set((capture.markerConsequences ?? []).map((m) => m.providerMarkerName))];
  const markerWord = source.provider === 'gmail'
    ? (markers.length === 1 ? 'Label' : 'Labels')
    : (markers.length === 1 ? 'Category' : 'Categories');
  const crmStage = capture.classificationSources?.commercialStage === 'crm'
    ? stageLabel(capture.commercialStage, vocab) : null;
  const reasonFor = (r: string) => (r === 'crm_stage_conflict' && crmStage
    ? `Your CRM already has this contact as ${crmStage}` : reasonText(r));
  const warnings = [...new Set(capture.reviewReasons.filter((r) => r !== 'review_mode').map(reasonFor))];

  return (
    <div className="min-w-0 space-y-5">
      {/* WHERE IT CAME FROM: once, in the drawer's header. */}
      <DrawerSource>
        <p
          className={WRAP} data-captured-from=""
          title={`Captured from ${PROVIDER_TEXT[source.provider]}${markers.length > 0 ? ` · ${markerWord}: ${markers.join(', ')}` : ''}`}
        >
          Captured from {PROVIDER_TEXT[source.provider]}
          {markers.length > 0 ? <> · {markerWord}: {markers.join(', ')}</> : null}
          {' · '}
          <time dateTime={capture.occurredAt ?? undefined}>{exactWhen(capture.occurredAt)}</time>
        </p>
      </DrawerSource>

      {/* Only what a person can act on: "review mode" is why everything is here, not a warning. */}
      {warnings.length > 0 ? (
        <div className="min-w-0 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">Check before approving</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5" aria-label="Why this needs a look">
            {warnings.map((t) => <li key={t} className={WRAP}>{t}</li>)}
          </ul>
        </div>
      ) : null}
      {/* Waiting for review is what every item here is; only another status is worth a badge. */}
      {capture.status !== 'review_required' || capture.mode === 'auto' || capture.executedAt ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          {capture.status !== 'review_required' ? <CaptureStatusBadge status={capture.status} /> : null}
          {capture.mode === 'auto' ? <span>Automatic mode</span> : null}
          {capture.executedAt ? <span>Sent {formatWhen(capture.executedAt)}</span> : null}
        </div>
      ) : null}

      <fieldset disabled={!editable} className="min-w-0 space-y-5">
        {/* PEOPLE FOUND */}
        <section className="min-w-0">
          <h4 className={h4}>People found</h4>
          <ul className="mt-2 space-y-2">
            {/* The mailbox owner (and members) stay on the message, never in the list of contacts. */}
            {capture.participants.filter((p) => !p.isInternal).map((p) => (
              <PersonRow
                key={p.id}
                p={p}
                d={draft.people[p.id]!}
                editable={editable}
                onChange={(x) => patchPerson(p.id, x)}
                deliveries={data.phoneDelivery?.[p.id]}
                phoneError={phoneError?.participantId === p.id ? phoneError : null}
                types={data.contactTypes}
                onShared={(a) => onShared(p.id, a)}
                {...typePropsFor(p)}
                sharedBusy={sharedBusy}
              />
            ))}
          </ul>
        </section>

        {/* COMPANY */}
        <section className="min-w-0">
          <h4 className={h4}>Company</h4>
          {capture.company ? (
            <div className="mt-2 space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block min-w-0 text-sm text-slate-700" htmlFor={`company-name-${capture.id}`}>
                  Name
                  <input
                    id={`company-name-${capture.id}`}
                    value={draft.companyName}
                    onChange={(e) => patch({ companyName: e.target.value })}
                    className={inputCls}
                  />
                </label>
                <label className="block min-w-0 text-sm text-slate-700" htmlFor={`company-web-${capture.id}`}>
                  Website
                  <input
                    id={`company-web-${capture.id}`}
                    type="url"
                    value={draft.companyWebsite}
                    onChange={(e) => patch({ companyWebsite: e.target.value })}
                    className={inputCls}
                  />
                </label>
              </div>
              {capture.company.domain ? (
                <p className={`text-xs text-slate-600 ${WRAP}`}>Domain: {capture.company.domain}</p>
              ) : null}
              {capture.company.evidence.length > 0 ? (
                <ul className="space-y-0.5 text-xs text-slate-600">
                  {capture.company.evidence.map((e, i) => (
                    <li key={`${e.kind}-${i}`} className={WRAP}>
                      <span className="text-slate-500">{EVIDENCE_TEXT[e.kind] ?? e.kind}:</span> {e.value}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-500">No company was found in this email.</p>
          )}
        </section>

        {/* CRM TAGS: one section, one state */}
        <section className="min-w-0" data-crm-tags-section="">
          <CrmTagPicker
            vocab={vocab}
            selected={tagIds}
            onChange={setTags}
            disabled={!editable}
            legend="CRM Tags"
            legendClassName={h4}
            hints={hints}
            empty="No CRM tags. Add one, or approve without tags."
          />
        </section>
        {/* INTERACTION */}
        <section className="min-w-0">
          <h4 className={h4}>Interaction</h4>
          <label className="mt-2 block text-sm text-slate-700" htmlFor={`intent-${capture.id}`}>Intent</label>
          <select
            id={`intent-${capture.id}`}
            value={draft.intent}
            onChange={(e) => patch({ intent: e.target.value as CaptureIntent })}
            className={inputCls}
          >
            {INTENTS.map((k) => <option key={k} value={k}>{INTENT_LABELS[k]}</option>)}
          </select>
          <label className="mt-3 block text-sm text-slate-700" htmlFor={`summary-${capture.id}`}>Summary</label>
          <textarea
            id={`summary-${capture.id}`}
            value={draft.summary}
            maxLength={420}
            rows={3}
            aria-describedby={`summary-count-${capture.id}`}
            onChange={(e) => patch({ summary: e.target.value })}
            className="mt-1 w-full min-w-0 rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500"
          />
          <p id={`summary-count-${capture.id}`} className="text-right text-xs text-slate-500">
            {draft.summary.length}/420
          </p>
          <p className="text-xs text-slate-500">Written to your CRM as part of the activity note.</p>
        </section>

      </fieldset>

      {/* THE EMAIL ITSELF */}
      <section className="min-w-0">
        <h4 className={h4}>Source message</h4>
        <p className={`mt-2 text-sm font-medium text-slate-900 ${WRAP}`}>{source.subject ?? 'No subject'}</p>
        <p className={`mt-1 text-xs text-slate-600 ${WRAP}`}>
          From: {source.fromDisplayName ? `${source.fromDisplayName} ` : ''}{source.from ? `<${source.from}>` : ''}
        </p>
        {source.to.length > 0 ? <p className={`text-xs text-slate-600 ${WRAP}`}>To: {source.to.join(', ')}</p> : null}
        {source.cc.length > 0 ? <p className={`text-xs text-slate-600 ${WRAP}`}>CC: {source.cc.join(', ')}</p> : null}
        <div className="mt-1 flex flex-wrap items-center gap-x-4">
          {source.authored ? (
            <button
              type="button"
              aria-expanded={showMessage}
              onClick={() => setShowMessage((v) => !v)}
              className="inline-flex min-h-11 items-center text-sm text-slate-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              {showMessage ? 'Hide message' : 'View message'}
            </button>
          ) : null}
          {source.webLink ? (
            <a
              href={source.webLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center text-sm text-slate-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              View original email
            </a>
          ) : null}
        </div>
        {source.redacted && !source.authored ? (
          <p className="mt-1 text-xs text-slate-500">
            The text of this email is no longer stored. Marked email text is kept for up to 90 days.
          </p>
        ) : null}
        {showMessage && source.authored ? (
          <div className="mt-2 space-y-2">
            <div className={`signal-message-body whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm text-slate-800 ${WRAP}`}>
              {source.authored}
            </div>
            {source.quoted ? (
              <details className="text-sm">
                <summary className="cursor-pointer py-2 text-slate-700">Earlier in the thread</summary>
                <div className={`whitespace-pre-wrap rounded-md border border-slate-200 p-3 text-xs text-slate-600 ${WRAP}`}>
                  {source.quoted}
                </div>
              </details>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* CRM CONSEQUENCE */}
      <section className="min-w-0 rounded-md bg-slate-50 p-3">
        <h4 className={h4}>CRM consequence</h4>
        {/* Already sent, only a Contact Type change left: approval makes just that change. */}
        {typeChanges.length > 0 && (consequence.length === 0 || capture.status !== 'review_required') ? (
          <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
            {typeChanges.map((l) => <li key={l} className={`font-medium ${WRAP}`} data-type-change="">{l}</li>)}
          </ul>
        ) : consequence.length === 0 ? (
          <p className="mt-1 text-sm text-slate-600">No one is included, so nothing would be sent.</p>
        ) : (
          <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
            {consequence.map((l) => <li key={l} className={WRAP}>{l}</li>)}
            <li className={WRAP}>
              Activity: {INTENT_LABELS[draft.intent]} note on {included.length} contact{included.length === 1 ? '' : 's'}
            </li>
            <li className={WRAP}>Tags: {tagLabels.length > 0 ? tagLabels.join(', ') : 'none'}</li>
            {typeChanges.map((l) => <li key={l} className={`font-medium ${WRAP}`} data-type-change="">{l}</li>)}
          </ul>
        )}
        {!data.crmWriteEnabled ? (
          <p className="mt-2 text-xs text-slate-500">
            CRM sending is switched off, so approving records your decision without sending anything.
          </p>
        ) : null}
      </section>

      {footer}
    </div>
  );
}

/**
 * One reviewer action on a shared address, as the participant edit the server reads. The
 * person's other unsaved changes travel with it, so nothing on screen is lost.
 */
export function sharedEdit(
  base: CaptureEdit['participants'][number] | undefined, id: string, d: PersonDraft, action: SharedAction,
): CaptureEdit['participants'][number] {
  const e: CaptureEdit['participants'][number] = { ...(base ?? { id, include: d.include }), id };
  if ('keapContactId' in action) return { ...e, keapContactId: action.keapContactId };
  // The name the person sees is the name confirmed.
  if (action.confirm && d.displayName.trim()) e.displayName = d.displayName.trim();
  return { ...e, include: action.confirm, confirmSharedAddress: action.confirm };
}

/** What a shared-address decision did, in a sentence. Nothing was sent by any of them. */
export function sharedOutcome(action: SharedAction): Outcome {
  if ('keapContactId' in action) return { tone: 'success', text: 'Matched to that Keap contact. Nothing has been sent yet.' };
  return action.confirm
    ? { tone: 'success', text: 'Confirmed. Keap was checked for this person; review the result, then approve. Nothing has been sent yet.' }
    : { tone: 'info', text: 'Skipped. This person won’t be sent.' };
}
